import { Info } from 'lucide-react';
import React from 'react';
import type { TeamMember } from '../../TeamTabContext';

export interface TeamRosterTableHeaderProps {
  highlightTeamRosterSection: boolean;
  teamMembers: TeamMember[];
  setTeamMembers: React.Dispatch<React.SetStateAction<TeamMember[]>>;
  canModifyTeam: boolean;
}

export const TeamRosterTableHeader: React.FC<TeamRosterTableHeaderProps> = ({
  highlightTeamRosterSection,
  teamMembers,
  setTeamMembers,
  canModifyTeam,
}) => {
  return (
    <div
      id="profile-team-roster-table-header"
      className="team-roster-table-grid team-roster-table-header"
    >
      {/*
        Bulk-toggle headers: clicking the checkbox flips the flag for
        every roster row. Like the per-row checkboxes, we only mutate
        local `teamMembers` state — persistence still goes through the
        existing "Save Capacity Settings" button, which compares against
        `originalTeamMembers` and PATCHes only changed members.
      */}
      <div className="team-roster-table-header-cell team-roster-table-cell--sticky-products">
        <span>Products</span>
        <span
          className="stat-hint-icon"
          title="Same list as header product switcher. Manual clears override sync."
          aria-label="Same list as header product switcher. Manual clears override sync."
        >
          <Info size={14} aria-hidden />
        </span>
      </div>
      {(() => {
        const total = teamMembers.length;
        const checkedCount = teamMembers.filter(
          (m) => m.includeInCapacity === true || m.includeInCapacity === undefined
        ).length;
        const allChecked = total > 0 && checkedCount === total;
        const someChecked = checkedCount > 0 && checkedCount < total;
        const disabled = !canModifyTeam || total === 0;
        return (
          <label
            className="team-roster-table-check-heading"
            style={{ cursor: disabled ? 'default' : 'pointer' }}
            title={disabled ? 'Capacity' : `Toggle Capacity for all (${checkedCount}/${total})`}
          >
            <input
              type="checkbox"
              checked={allChecked}
              ref={(el) => {
                if (el) el.indeterminate = someChecked;
              }}
              disabled={disabled}
              onChange={(e) => {
                const next = e.target.checked;
                setTeamMembers((prev) => prev.map((m) => ({ ...m, includeInCapacity: next })));
              }}
              style={{
                width: '18px',
                height: '18px',
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
              aria-label="Toggle Capacity for all team members"
            />
            Capacity
          </label>
        );
      })()}
      {(() => {
        const total = teamMembers.length;
        const checkedCount = teamMembers.filter(
          (m) => m.includeInHistoricalCapacity === true
        ).length;
        const allChecked = total > 0 && checkedCount === total;
        const someChecked = checkedCount > 0 && checkedCount < total;
        const disabled = !canModifyTeam || total === 0;
        return (
          <label
            className="team-roster-table-check-heading"
            style={{ cursor: disabled ? 'default' : 'pointer' }}
            title={disabled ? 'Historical' : `Toggle Historical for all (${checkedCount}/${total})`}
          >
            <input
              type="checkbox"
              checked={allChecked}
              ref={(el) => {
                if (el) el.indeterminate = someChecked;
              }}
              disabled={disabled}
              onChange={(e) => {
                const next = e.target.checked;
                setTeamMembers((prev) =>
                  prev.map((m) => ({ ...m, includeInHistoricalCapacity: next }))
                );
              }}
              style={{
                width: '18px',
                height: '18px',
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
              aria-label="Toggle Historical capacity for all team members"
            />
            Historical
          </label>
        );
      })()}
      {(() => {
        const total = teamMembers.length;
        const checkedCount = teamMembers.filter((m) => m.excludeFromDistribution === true).length;
        const allChecked = total > 0 && checkedCount === total;
        const someChecked = checkedCount > 0 && checkedCount < total;
        const disabled = !canModifyTeam || total === 0;
        return (
          <label
            className="team-roster-table-check-heading"
            style={{ cursor: disabled ? 'default' : 'pointer' }}
            title={
              disabled
                ? 'Excl. Forecast'
                : `Toggle Exclude from Forecast for all (${checkedCount}/${total}); applies even if someone is not on this product or deactivated`
            }
          >
            <input
              type="checkbox"
              checked={allChecked}
              ref={(el) => {
                if (el) el.indeterminate = someChecked;
              }}
              disabled={disabled}
              onChange={(e) => {
                const next = e.target.checked;
                setTeamMembers((prev) =>
                  prev.map((m) => ({ ...m, excludeFromDistribution: next }))
                );
              }}
              style={{
                width: '18px',
                height: '18px',
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
              aria-label="Toggle Exclude from Forecast for all team members"
            />
            Excl. Forecast
          </label>
        );
      })()}
      <div className="team-roster-table-header-cell">Start in team</div>
      <div className="team-roster-table-header-cell">End in team</div>
      <div
        id="profile-team-roster-role-header"
        className={`team-roster-table-header-cell${highlightTeamRosterSection ? ' profile-team-roster-highlight-role' : ''}`}
      >
        Role
      </div>
      <div className="team-roster-table-header-cell">Name</div>
      <div className="team-roster-table-header-cell">Email</div>
      <div className="team-roster-table-header-cell">
        <span>Jira</span>
        <span
          className="stat-hint-icon"
          title="Jira account ID or username. Leave empty for deactivated users."
          aria-label="Jira account ID or username. Leave empty for deactivated users."
        >
          <Info size={14} aria-hidden />
        </span>
      </div>
      <div className="team-roster-table-header-cell team-roster-table-header-cell--end">
        Hours/Day
      </div>
      <div className="team-roster-table-header-cell team-roster-table-header-cell--center team-roster-table-cell--sticky-actions">
        Actions
      </div>
    </div>
  );
};
